/**
 * OrgStatsRepository – lecture orgStatsDaily sur une période.
 * Zéro scan orders : l’UI analytics consomme uniquement ces stats pré-calculées.
 */

import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';

const COLLECTION = 'orgStatsDaily';

export interface OrgStatsDailyDoc {
    orgId: string;
    day: string;
    timezone: string;
    ordersPaidCount: number;
    revenueGrossCents: number;
    revenueNetCents: number;
    commissionCents: number;
    itemsPaidCount: number;
    refundsCount: number;
    refundsCents: number;
    activeCustomersCount: number;
    newCustomersCount: number;
    returningCustomersCount: number;
    revenueNewCustomersCents: number;
    revenueReturningCustomersCents: number;
    paymentsSucceeded: number;
    paymentsFailed: number;
    paymentMethodCount: Record<string, number>;
    paymentMethodRevenueCents: Record<string, number>;
    paymentDelaySumSeconds: number;
    paymentDelayCount: number;
    delayBuckets: Record<string, number>;
    latePaymentsCount: number;
    shippingOrdersCount: number;
    shippingRevenueCents: number;
    carrierCount: Record<string, number>;
    shippingMethodCount: Record<string, number>;
    homeDeliveryCount: number;
    relayDeliveryCount: number;
    ordersWithTrackingCount: number;
    countryCount: Record<string, number>;
    countryRevenueCents: Record<string, number>;
    cityCount: Record<string, number>;
    cityRevenueCents: Record<string, number>;
    ordersByHour: Record<string, number>;
    revenueByHourCents: Record<string, number>;
    ordersByQuarterHour: Record<string, number>;
    revenueByQuarterHourCents: Record<string, number>;
}

function fromFirestore(data: Record<string, unknown>): OrgStatsDailyDoc {
    return {
        orgId: (data.orgId as string) ?? '',
        day: (data.day as string) ?? '',
        timezone: (data.timezone as string) ?? 'Europe/Paris',
        ordersPaidCount: (data.ordersPaidCount as number) ?? 0,
        revenueGrossCents: (data.revenueGrossCents as number) ?? 0,
        revenueNetCents: (data.revenueNetCents as number) ?? 0,
        commissionCents: (data.commissionCents as number) ?? 0,
        itemsPaidCount: (data.itemsPaidCount as number) ?? 0,
        refundsCount: (data.refundsCount as number) ?? 0,
        refundsCents: (data.refundsCents as number) ?? 0,
        activeCustomersCount: (data.activeCustomersCount as number) ?? 0,
        newCustomersCount: (data.newCustomersCount as number) ?? 0,
        returningCustomersCount: (data.returningCustomersCount as number) ?? 0,
        revenueNewCustomersCents: (data.revenueNewCustomersCents as number) ?? 0,
        revenueReturningCustomersCents: (data.revenueReturningCustomersCents as number) ?? 0,
        paymentsSucceeded: (data.paymentsSucceeded as number) ?? 0,
        paymentsFailed: (data.paymentsFailed as number) ?? 0,
        paymentMethodCount: (data.paymentMethodCount as Record<string, number>) ?? {},
        paymentMethodRevenueCents: (data.paymentMethodRevenueCents as Record<string, number>) ?? {},
        paymentDelaySumSeconds: (data.paymentDelaySumSeconds as number) ?? 0,
        paymentDelayCount: (data.paymentDelayCount as number) ?? 0,
        delayBuckets: (data.delayBuckets as Record<string, number>) ?? {},
        latePaymentsCount: (data.latePaymentsCount as number) ?? 0,
        shippingOrdersCount: (data.shippingOrdersCount as number) ?? 0,
        shippingRevenueCents: (data.shippingRevenueCents as number) ?? 0,
        carrierCount: (data.carrierCount as Record<string, number>) ?? {},
        shippingMethodCount: (data.shippingMethodCount as Record<string, number>) ?? {},
        homeDeliveryCount: (data.homeDeliveryCount as number) ?? 0,
        relayDeliveryCount: (data.relayDeliveryCount as number) ?? 0,
        ordersWithTrackingCount: (data.ordersWithTrackingCount as number) ?? 0,
        countryCount: (data.countryCount as Record<string, number>) ?? {},
        countryRevenueCents: (data.countryRevenueCents as Record<string, number>) ?? {},
        cityCount: (data.cityCount as Record<string, number>) ?? {},
        cityRevenueCents: (data.cityRevenueCents as Record<string, number>) ?? {},
        ordersByHour: (data.ordersByHour as Record<string, number>) ?? {},
        revenueByHourCents: (data.revenueByHourCents as Record<string, number>) ?? {},
        ordersByQuarterHour: (data.ordersByQuarterHour as Record<string, number>) ?? {},
        revenueByQuarterHourCents: (data.revenueByQuarterHourCents as Record<string, number>) ?? {},
    };
}

/**
 * Récupère les stats daily pour une org et une période (jours inclus).
 * startDay / endDay au format YYYY-MM-DD.
 */
export async function getOrgStatsDailyRange(
    orgId: string,
    startDay: string,
    endDay: string
): Promise<OrgStatsDailyDoc[]> {
    const q = query(
        collection(db, COLLECTION),
        where('orgId', '==', orgId),
        where('day', '>=', startDay),
        where('day', '<=', endDay),
        orderBy('day', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => fromFirestore(d.data() as Record<string, unknown>));
}
